import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { cn } from "../../../../lib/utils";
import { useClaimMotion } from "./claimMotion";

/**
 * Where the claim has got to, across the five stages CRM reports.
 *
 * The old stepper drew a 6px dot per stage with a hairline between them and
 * hung the labels off to one side with a negative margin, so on a phone the
 * words sat under the wrong dots. It also had no idea it was a stepper as far
 * as assistive tech was concerned.
 *
 * This keeps the same five-across shape and the same brand fill, but the
 * segments now animate as they complete, and each stage reads as complete /
 * current / upcoming at a glance.
 *
 * On a phone five labels do not fit under five dots ("Registration" alone
 * needs more than a fifth of 360px), and a track that scrolls sideways hides
 * where the claim is. So below 768px the dots span the full width with no
 * labels under them, and one line above names the current stage, its position
 * and what comes next. The labels stay in the list for screen readers.
 */
const ClaimsStepper = ({ steps, activeStep }) => {
  const translate = useLanguageTranslate();
  const { t } = useTranslation();
  const { reduceMotion } = useClaimMotion();
  const activeIndex = steps.findIndex((step) => step?.key === activeStep?.key);
  const current = steps[activeIndex];
  const next = steps[activeIndex + 1];

  return (
    <div>
      {current && (
        // Phone only, and hidden from assistive tech: the list below already
        // marks the current step with aria-current.
        <div aria-hidden="true" className="mb-4 hidden max767:block">
          <p className="text-base font-semibold leading-6 text-[#1A2024]">
            {translate(current.title_en, current.title_ar)}
          </p>
          <p className="mt-0.5 text-[13px] leading-[18px] text-[#6C7A83]">
            {translate(
              `Stage ${activeIndex + 1} of ${steps.length}`,
              `المرحلة ${activeIndex + 1} من ${steps.length}`
            )}
            {next &&
              ` · ${translate(
                `Next: ${next.title_en}`,
                `التالي: ${next.title_ar}`
              )}`}
          </p>
        </div>
      )}
      <ol
        aria-label={t("motorInsurance.trackClaimFlow.breadcrumb.claim_details")}
        className="flex w-full"
      >
        {steps.map((step, index) => {
          const isComplete = activeIndex > -1 && index < activeIndex;
          const isCurrent = index === activeIndex;
          // The segment leading INTO this step is filled once the step before it
          // is behind us, so the line always stops at the current stage.
          const segmentFilled = activeIndex > -1 && index <= activeIndex;

          return (
            <li
              key={step.key}
              aria-current={isCurrent ? "step" : undefined}
              className="flex min-w-[92px] flex-1 flex-col items-center gap-2 max767:min-w-0"
            >
              <div className="flex w-full items-center">
                {/* Leading half-segment — hidden on the first step so the track
                    starts at the dot rather than floating off the edge. */}
                <span
                  className={cn(
                    "relative h-0.5 flex-1 rounded-full bg-[#DDE2E4]",
                    index === 0 && "invisible"
                  )}
                >
                  <motion.span
                    className="absolute inset-0 origin-left rounded-full bg-[#00A9E7] rtl:origin-right"
                    initial={false}
                    animate={{ scaleX: segmentFilled ? 1 : 0 }}
                    transition={{
                      duration: reduceMotion ? 0 : 0.45,
                      ease: [0.22, 0.61, 0.36, 1],
                      delay: reduceMotion ? 0 : index * 0.06,
                    }}
                  />
                </span>

                <span
                  className={cn(
                    "z-10 flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-300",
                    isComplete && "border-[#00A9E7] bg-[#00A9E7]",
                    isCurrent && "border-[#00A9E7] bg-white",
                    !isComplete && !isCurrent && "border-[#DDE2E4] bg-white"
                  )}
                >
                  {isComplete && (
                    <Check
                      className="size-3 text-white"
                      strokeWidth={3}
                      aria-hidden="true"
                    />
                  )}
                  {isCurrent && (
                    <motion.span
                      className="size-2 rounded-full bg-[#00A9E7]"
                      initial={reduceMotion ? false : { scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 480, damping: 22 }}
                    />
                  )}
                </span>

                <span
                  className={cn(
                    "relative h-0.5 flex-1 rounded-full bg-[#DDE2E4]",
                    index === steps.length - 1 && "invisible"
                  )}
                >
                  <motion.span
                    className="absolute inset-0 origin-left rounded-full bg-[#00A9E7] rtl:origin-right"
                    initial={false}
                    animate={{ scaleX: isComplete ? 1 : 0 }}
                    transition={{
                      duration: reduceMotion ? 0 : 0.45,
                      ease: [0.22, 0.61, 0.36, 1],
                      delay: reduceMotion ? 0 : index * 0.06,
                    }}
                  />
                </span>
              </div>

              <span
                className={cn(
                  "px-1 text-center text-xs leading-4 sm:text-sm sm:leading-5 max767:sr-only",
                  isCurrent
                    ? "font-semibold text-[#1A2024]"
                    : isComplete
                    ? "font-medium text-[#48535B]"
                    : "font-normal text-[#9AA6AC]"
                )}
              >
                {translate(step.title_en, step.title_ar)}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default ClaimsStepper;
